<template>
  <div>
    <div v-if="$userInfo.isSupportUser || $userInfo.isCustomerAdministrator || $userInfo.isSystemAdministrator"
      class="tile is-parent pl-1">
      <article class="tile is-child box">
        <div class="columns is-flex is-align-items-center">
          <div class="column">
            <p class="title">Integration</p>
          </div>
          <div v-if="!hasError && showSavingStatus && !toastNotificationEnabled"
            class="column is-flex is-justify-content-flex-end is-align-items-center">
            <span v-if="saving"
              class="button is-loading is-white"
              style="height: 27px;" />
            <span v-else
              class="text has-text-success">
              <span class="mso mso-18px">check_circle</span>
            </span>
            <span class="is-size-6"
              :class="saving ? 'has-text-warning' : 'has-text-success'">{{ savingStatus }}</span>
          </div>
        </div>

        <div class="field">
          <div class="tabs is-boxed">
            <ul>
              <li v-for="(companyExternalSetting, index) in companyExternalSettings"
                :key="index"
                :class="{ 'is-active' : activeTabIndex === index }">
                <a @click="activeTabIndex = index">{{ companyExternalSetting.settingName }}</a>
              </li>
            </ul>
          </div>
        </div>

        <div class="field columns"
          v-if="activeCompanyExternalSetting">
          <div class="column is-half">
            <div class="field"
              v-for="(column, index) in $options.columns.left"
              :key="index">
              <label class="label">{{ activeCompanyExternalSetting[column.label] }}</label>

              <div v-if="requirePasswordInvisible(column)"
                class="field has-addons">
                <div class="control is-expanded">
                  <input class="input"
                    :type="showPassword ? 'text' : 'password'"
                    v-model="activeCompanyExternalSetting[column.value]"
                    :disabled="!passwordEnabled">
                  <span v-if="!$v.activeCompanyExternalSetting.password.maxLength"
                    class="help is-danger ml-1">Password must not exceed 50 characters.</span>
                </div>

                <p class="control">
                  <button class="button"
                    @click="passwordEnabled=!passwordEnabled">
                    <span class="icon has-text-primary">
                      <span class="mso mso-24px">edit</span>
                    </span>
                  </button>
                </p>
                <p class="control">
                  <button class="button"
                    @click="showPassword=!showPassword">
                    <span class="icon has-text-primary">
                      <span class="mso mso-24px">
                        {{ showPassword ? 'visibility' : 'visibility_off' }}
                      </span>
                    </span>
                  </button>
                </p>
              </div>

              <div v-else
                class="field has-addons">
                <div class="control is-expanded">
                  <input class="input"
                    type="text"
                    v-model="activeCompanyExternalSetting[column.value]"
                    :disabled="requirePasswordGeneration(column) || isDiable(column)">
                </div>

                <p v-if="requirePasswordGeneration(column)"
                  class="control">
                  <button class="button is-primary"
                    @click="randomPassword(32, 6)">
                    Generate
                  </button>
                </p>
              </div>
            </div>
          </div>

          <div class="column is-half">
            <div class="field"
              v-for="(column, index) in $options.columns.right"
              :key="index">
              <label class="label">{{ activeCompanyExternalSetting[column.label] }}
                <span v-if="isEstimageSettingPage && activeCompanyExternalSetting[column.label] === 'Enable Estimage Online'"
                  class="has-text-weight-normal is-italic is-size-7">
                  (<span v-if="$userInfo.isSupportUser"><span class="has-text-weight-bold">2</span> = Test, </span>
                  <span class="has-text-weight-bold">1</span> = On and <span class="has-text-weight-bold">0</span> = Off)
                </span>
                <span v-if="isEstimageSettingPage && activeCompanyExternalSetting[column.label] === 'Auto Sync'"
                  class="has-text-weight-normal is-italic is-size-7">
                  (<span class="has-text-weight-bold">1</span> = On and <span class="has-text-weight-bold">0</span> = Off)
                </span>
                <span v-if="isPartsCheckSettingPage && activeCompanyExternalSetting[column.label] === 'Auto Sync'"
                  class="has-text-weight-normal is-italic is-size-7">
                  (<span class="has-text-weight-bold">2</span> = On and <span class="has-text-weight-bold">0</span> = Off)
                </span>
                <span v-if="isPartsCheckSmartReceiptingColumn(column)"
                  class="has-text-weight-normal is-italic is-size-7">
                  (<span class="has-text-weight-bold">1</span> = On and <span class="has-text-weight-bold">0</span> = Off, requires Auto Sync)
                </span>
              </label>
              <input class="input"
                type="text"
                v-model="activeCompanyExternalSetting[column.value]"
                :disabled="requirePasswordGeneration(column) || isDiable(column)">
              <template v-if="isPartsCheckSmartReceiptingColumn(column)">
                <span v-if="!isPartsCheckAutoSyncOn"
                  class="help is-italic has-text-grey">Set Auto Sync to 2 to turn on Smart Receipted Documents.</span>
                <span v-else-if="!$v.activeCompanyExternalSetting.property3.smartReceiptingValue"
                  class="help is-danger">Enter 1 (On) or 0 (Off).</span>
              </template>
            </div>
          </div>
        </div>
        <div class="columns"
          v-if="isEstimageSettingPage">
          <div class="column is-half">
            <div class="flex-row">
              <div class="mr-4">
                <button class="button is-primary"
                  @click="connectEstimage(false)">
                  <span class="icon">
                    <span class="mso mso-18px">cached</span>
                  </span>
                  <span><span v-if="activeCompanyExternalSetting.property4 === '1'">Re-</span>Connect to Estimage</span>
                </button>
                <span v-if="activeCompanyExternalSetting.property4 === '1'"
                  class="help is-italic has-text-grey">Last connected: {{ $filters.formatDateTimeLocale(dateTimeToLocal(activeCompanyExternalSetting.modifiedDate), $userInfo.locale) }} </span>
              </div>
              <div class="mr-4"
                v-if="$userInfo.isSupportUser">
                <button class="button is-primary"
                  @click="connectEstimage(true)">
                  <span class="icon">
                    <span class="mso mso-18px">cached</span>
                  </span>
                  <span><span v-if="activeCompanyExternalSetting.property4 === '2'">Re-</span>Connect to Test Estimage</span>
                </button>
                <span v-if="activeCompanyExternalSetting.property4 === '2'"
                  class="help is-italic has-text-grey">Last connected: {{ $filters.formatDateTimeLocale(dateTimeToLocal(activeCompanyExternalSetting.modifiedDate), $userInfo.locale) }} </span>
              </div>
            </div>
          </div>
          <div class="column is-half is-flex is-justify-content-right is-align-items-center">
            <span class="icon is-small has-text-primary mr-1">
              <span class="mso mso-18px">info</span>
            </span>
            <span class="help is-primary is-italic">Reconnection may periodically be required.</span>
          </div>
        </div>
      </article>
    </div>
    <div v-else
      class="is-flex is-justify-content-center pt-5 ">
      <div class="notification is-danger is-flex is-justify-content-center is-size-4"
        style="width: 50%">
        You do not have permissions on this page
      </div>
    </div>
  </div>
</template>

<script>
import _cloneDeep from 'lodash/cloneDeep'
import _debounce from 'lodash/debounce'
import StoreMixin from './storeMixin'
import { DeepDiff } from 'deep-diff'
import { ExternalSettingTypes } from '@/enums'
import { EstImageService } from '@/services'
import { maxLength } from 'vuelidate/lib/validators'

export default {
  name: 'CompanyIntegrationSetting',
  mixins: [StoreMixin],
  props: {
    defaultActiveTabIndex: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      activeTabIndex: 0,
      autoUpdate: null,
      companyExternalSettings: null,
      lastModified: null,
      passwordEnabled: false,
      showPassword: false,
      timeOutId: null,
      isNew: false
    }
  },
  columns: {
    left: [
      { label: 'tagLabel', value: 'tag' },
      { label: 'usernameLabel', value: 'username' },
      { label: 'passwordLabel', value: 'password' },
      { label: 'servernameLabel', value: 'serverName' },
      { label: 'portLabel', value: 'port' }
      // { label: 'tokenLabel', value: 'token' }
    ],
    right: [
      { label: 'property1Label', value: 'property1' },
      { label: 'property2Label', value: 'property2' },
      { label: 'property3Label', value: 'property3' },
      { label: 'property4Label', value: 'property4' },
      { label: 'property5Label', value: 'property5' }
    ]
  },
  computed: {
    activeCompanyExternalSetting() {
      if (this.companyExternalSettings && this.companyExternalSettings[this.activeTabIndex]) {
        return this.companyExternalSettings[this.activeTabIndex]
      }
      return null
    },
    externalSettingTypes: function () {
      return ExternalSettingTypes
    },
    isEstimageSettingPage() {
      console.log('active setting: ', this.activeCompanyExternalSetting)
      return this.activeCompanyExternalSetting && this.activeCompanyExternalSetting.settingName === this.externalSettingTypes.SETTING_ESTIMAGE
    },
    isPartsCheckSettingPage() {
      return this.activeCompanyExternalSetting && this.activeCompanyExternalSetting.settingName === this.externalSettingTypes.SETTING_PARTSCHECK
    },
    // PartsCheck: Property5 = Auto Sync (2 = On), Property3 = Smart Receipted Documents (1 = On).
    // Smart receipting runs inside the auto-sync chain, so it depends on Auto Sync being on.
    partsCheckExternalSetting() {
      return (this.companyExternalSettings || []).find((s) => s.settingName === this.externalSettingTypes.SETTING_PARTSCHECK) || null
    },
    partsCheckAutoSync() {
      return this.partsCheckExternalSetting ? this.partsCheckExternalSetting.property5 : undefined
    },
    isPartsCheckAutoSyncOn() {
      return (this.partsCheckAutoSync || '').trim() === '2'
    },
    isEstimageEnabledLabel() {
      return this.activeCompanyExternalSetting && this.activeCompanyExternalSetting[column.label] === this.externalSettingTypes.SETTING_ESTIMAGE
    }
  },
  watch: {
    companyExternalSettings: {
      handler: function (val) {
        this.saveSnapshot({ entity: _cloneDeep(val) })
        if (typeof this.autoUpdate === 'function') this.autoUpdate(val)
      },
      deep: true
    },
    isEstimageSettingPage(val) {
      if (this.lastModified !== null) {
        if (val) {
          console.log('watch is estimage true')
          this.fetchEstimageSetting()
        } else {
          console.log('watch is estimage false')
          this.stopFetchEstimageSetting()
        }
      }
    }
  },
  async created() {
    this.activeTabIndex = this.defaultActiveTabIndex
    this.$showSpinner()
    if (this.$route.params.companyId === 'new') {
      this.isNew = true
      const newCompany = this.$store.getters['companysetting/newCompany']
      if (newCompany && newCompany.companyExternalSettings) {
        this.companyExternalSettings = _cloneDeep(newCompany.companyExternalSettings)
      } else this.companyExternalSettings = _cloneDeep(await this.getStoreItem({ serviceFunction: 'getNewCompanyExternalSettings' }))
    } else {
      this.companyExternalSettings = _cloneDeep(
        await this.getStoreItem({ serviceFunction: 'getCompanyExternalSettings', params: this.$route.params.companyId })
      )
    }
    if (this.isNew) this.setStateProperty({ key: 'companyExternalSettings', value: this.companyExternalSettings })
    this.initializeAutoUpdate()
    this.$hideSpinner()
  },
  methods: {
    dateTimeToLocal(datetime) {
      const z = datetime.includes('Z') ? '' : 'Z'
      return datetime + z
    },
    disableAutoUpdate() {
      if (typeof this.autoUpdate === 'function' && typeof this.autoUpdate.cancel === 'function') {
        this.autoUpdate.cancel()
      }
      this.autoUpdate = () => {}
    },
    disableFetchEstimageSetting() {
      console.log('disable fetch estimage')
      if (typeof this.fetchEstimageSetting === 'function' && typeof this.fetchEstimageSetting.cancel === 'function') {
        this.fetchEstimageSetting.cancel()
      }
      this.fetchEstimageSetting = () => {}
    },
    async fetchEstimageSetting() {
      console.log('Fetching company external settings')
      const settings = _cloneDeep(await this.getStoreItem({ serviceFunction: 'getCompanyExternalSettings', params: this.$route.params.companyId }))
      this.companyExternalSettings[this.activeTabIndex].modifiedDate = settings[this.activeTabIndex].modifiedDate
      if (this.companyExternalSettings[this.activeTabIndex].property4 !== settings[this.activeTabIndex].property4) {
        await this.$store.dispatch('company/getCompanySetting')
      }
      this.companyExternalSettings[this.activeTabIndex].property4 = settings[this.activeTabIndex].property4

      if (this.lastModified === this.activeCompanyExternalSetting.modifiedDate) {
        // Schedule the next fetch after 5 seconds (5000 ms)
        if (this.isEstimageSettingPage) {
          this.timeOutId = setTimeout(this.fetchEstimageSetting, 1000 * 5)
          console.log('keep on checking ' + this.timeOutId)
        }
      } else {
        this.lastModified = null
        this.stopFetchEstimageSetting()
      }
    },
    stopFetchEstimageSetting() {
      clearTimeout(this.timeOutId) // Stop the timeout
      this.timeOutId = null
    },
    initializeAutoUpdate() {
      this.autoUpdate = _debounce(async (value) => {
        this.snapshotDiff && (await this.updateExternalSetting(value))
      }, this.debounceDelay)
    },
    randomPassword(length, nonAlphaLength) {
      var chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOP1234567890'
      var nonAlphaChars = '!@#$%^*()_+~|}{[]:;?,./-='
      var pass = ''
      for (var x = 0; x < length; x++) {
        var i = Math.floor(Math.random() * chars.length)
        pass += chars.charAt(i)
      }
      for (var y = 0; y < nonAlphaLength; y++) {
        var n = Math.floor(Math.random() * nonAlphaChars.length)
        var pos = Math.floor(Math.random() * pass.length)
        pass = pass.substring(0, pos) + nonAlphaChars.charAt(n) + pass.substring(pos + nonAlphaChars.charAt(n).length)
      }
      this.activeCompanyExternalSetting['password'] = pass
    },
    requirePasswordGeneration(column) {
      const integrations = ['BodyNet', 'WebTrim', 'Planning Plus']
      return column.value == 'password' && integrations.some((i) => i == this.activeCompanyExternalSetting['settingName'])
    },
    requirePasswordInvisible(column) {
      const integrations = ['ORM', 'PNET', 'Audatex']
      return column.value == 'password' && integrations.some((i) => i == this.activeCompanyExternalSetting['settingName'])
    },
    isDiable(column) {
      if (this.isPartsCheckSmartReceiptingColumn(column)) {
        return !this.isPartsCheckAutoSyncOn
      }
      const integrations = ['Estimage']
      return column.value == 'property4' && integrations.some((i) => i == this.activeCompanyExternalSetting['settingName'])
    },
    isPartsCheckSmartReceiptingColumn(column) {
      return this.isPartsCheckSettingPage && column.value === 'property3'
    },
    async updateExternalSetting(externalSetting) {
      if (!this.snapshotDiff) return
      this.$v.$touch()
      if (this.$v.$error) return
      if (this.isNew) {
        this.setStateProperty({ key: 'companyExternalSettings', value: externalSetting })
        return
      }

      for (let item of externalSetting) {
        const [baseItem] = this.baseSnapshot.filter((i) => i.companyExternalSettingId == item.companyExternalSettingId)
        const diff = DeepDiff.diff(baseItem, item)
        item.deepDiff = diff ? JSON.stringify(diff) : null
      }
      this.companyExternalSettings = _cloneDeep(
        await this.editStoreItem({
          serviceFunction: 'updateCompanyExternalSetting',
          entity: externalSetting,
          params: this.$route.params.companyId
        })
      )

      if (this.toastNotificationEnabled) {
        this.openToastNotification()
      }
    },
    popupWindow(url, windowName, win, w, h) {
      const y = win.top.outerHeight / 2 + win.top.screenY - h / 2
      const x = win.top.outerWidth / 2 + win.top.screenX - w / 2
      return win.open(
        url,
        windowName,
        `toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no, width=${w}, height=${h}, top=${y}, left=${x}`
      )
    },
    async connectEstimage(isTest = false) {
      this.$showSpinner()
      if (this.activeCompanyExternalSetting.username === null || this.activeCompanyExternalSetting.username === '') {
        this.$notification.openNotificationWithType('danger', 'Estimage', 'Username cannot be blank!')
        this.$hideSpinner()
        return false
      }

      if (this.snapshotDiff) {
        await this.updateExternalSetting(this.companyExternalSettings)
      }

      const estImageConfig = await EstImageService.getEstimageConfig()
      const estImageUrl = isTest ? estImageConfig.estImageUrlTest : estImageConfig.estImageUrl
      const estimageClientId = isTest ? estImageConfig.estImageClientIdTest : estImageConfig.estImageClientId
      this.lastModified = this.activeCompanyExternalSetting.modifiedDate
      this.fetchEstimageSetting()

      const url = `${estImageUrl}/oauth/authorize?response_type=code&client_id=${estimageClientId}&redirect_uri=${estImageConfig.estImageRedirectUrl}/auth`

      const w = 800
      const h = 650
      const y = window.top.outerHeight / 2 + window.top.screenY - h / 2
      const x = window.top.outerWidth / 2 + window.top.screenX - w / 2
      this.$hideSpinner()
      if (!estImageUrl || !estimageClientId || !estImageConfig.estImageRedirectUrl) {
        this.$notification.openNotificationWithType('danger', 'Estimage', 'Could not connect to EstImage. Config is missing.')
      } else {
        return window.open(
          url,
          'Estimage Online Authorisation',
          `toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no, width=${w}, height=${h}, top=${y}, left=${x}`
        )
      }
    }
  },
  validations() {
    return {
      activeCompanyExternalSetting: {
        password: { maxLength: maxLength(50) },
        property3: {
          // Only PartsCheck gives Property3 a meaning (Smart Receipted Documents): blank, 0 or 1.
          smartReceiptingValue: (value) => !this.isPartsCheckSettingPage || !value || ['0', '1'].includes(value.trim())
        }
      }
    }
  },
  async beforeRouteLeave(to, from, next) {
    this.disableAutoUpdate()
    this.disableFetchEstimageSetting()
    this.toastNotificationEnabled = true
    await this.updateExternalSetting(this.companyExternalSettings)
    this.clearSnapshots()
    this.toastNotificationEnabled = false
    next()
  }
}
</script>

<style lang="scss" scoped>
.tabs ul {
  width: 10em;
}

div.flex-row {
  display: flex;
  flex-direction: row;
}
</style>